import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api'
import { defaultConfig, VIZ_DEFAULT_SIZE, GRID_COLS } from '@/config/widgetSources'

/**
 * Dashboard-Layout v2:
 * {
 *   version: 2,
 *   sections: [{
 *     id, title, color, collapsed,
 *     layout: [{ i, x, y, w, h, viz, source, config }]
 *   }]
 * }
 *
 * Grid pro Section: 24 Spalten × 40px Zeilen (siehe widgetSources.js).
 * v1-Layouts (flaches Array mit `type`) werden beim Laden migriert.
 */

const uid = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`

// ── v1 → v2: Alt-Widget-Typen → generisches Modell ──────────────────────────
export const LEGACY_MAP = {
  'welcome':                { viz: 'info',    source: null },
  // HackWatch / Threat News
  'hack-last-posts':        { viz: 'table',   source: 'posts-latest' },
  'hack-worldmap':          { viz: 'map',     source: 'posts-countries-map' },
  'hack-recent-activity':   { viz: 'feed',    source: 'hackwatch-activity' },
  'hack-group-activity':    { viz: 'table',   source: 'groups-hacked-7d', config: { days: 7 } },
  'hack-posts-summary':     { viz: 'summary', source: 'posts-summary' },
  'hack-group-summary':     { viz: 'summary', source: 'groups-summary' },
  'hack-posts-total':       { viz: 'kpi',     source: 'posts-summary',  config: { field: 'total_posts' } },
  'hack-posts-24h':         { viz: 'kpi',     source: 'posts-summary',  config: { field: 'posts_last_day' } },
  'hack-posts-7d':          { viz: 'kpi',     source: 'posts-summary',  config: { field: 'posts_last_week' } },
  'hack-posts-30d':         { viz: 'kpi',     source: 'posts-summary',  config: { field: 'posts_last_month' } },
  'hack-groups-total':      { viz: 'kpi',     source: 'groups-summary', config: { field: 'total_hacker_groups' } },
  'hack-groups-members':    { viz: 'kpi',     source: 'groups-summary', config: { field: 'total_members' } },
  'hack-groups-locations':  { viz: 'kpi',     source: 'groups-summary', config: { field: 'total_locations' } },
  'hack-groups-profiles':   { viz: 'kpi',     source: 'groups-summary', config: { field: 'total_profiles' } },
  // SIEM / Detection
  'siem-recent-activity':   { viz: 'feed',    source: 'siem-activity' },
  'siem-changes-chart':     { viz: 'line',    source: 'workflow-changes' },
  'siem-summary':           { viz: 'summary', source: 'workflows-summary' },
  'siem-wf-total':          { viz: 'kpi',     source: 'workflows-summary', config: { field: 'total_workflows' } },
  'siem-wf-critical':       { viz: 'kpi',     source: 'workflows-summary', config: { field: 'critical_workflows' } },
  'siem-wf-high':           { viz: 'kpi',     source: 'workflows-summary', config: { field: 'high_workflows' } },
  'siem-wf-medium':         { viz: 'kpi',     source: 'workflows-summary', config: { field: 'medium_workflows' } },
  'siem-wf-low':            { viz: 'kpi',     source: 'workflows-summary', config: { field: 'low_workflows' } },
  'siem-wf-featured':       { viz: 'kpi',     source: 'workflows-summary', config: { field: 'featured_workflows' } },
  // News
  'news-top':               { viz: 'table',   source: 'news-latest' },
  // Kunden / Partner Customers
  'customers-summary':      { viz: 'summary', source: 'customers-summary' },
  'customers-top10':        { viz: 'table',   source: 'customers-top10' },
  'customers-total':        { viz: 'kpi',     source: 'customers-summary', config: { field: 'total_customers' } },
  'customers-active':       { viz: 'kpi',     source: 'customers-summary', config: { field: 'active_customers' } },
  'customers-confirmed':    { viz: 'kpi',     source: 'customers-summary', config: { field: 'total_confirmed_matches' } },
  'customers-pending':      { viz: 'kpi',     source: 'customers-summary', config: { field: 'total_pending_matches' } },
}

// v1: 12 Spalten × 120px-Zeilen → v2: 24 Spalten × 40px-Zeilen
function migrateV1Item(item) {
  const legacy = LEGACY_MAP[item.type]
  if (!legacy) return null
  return {
    i: item.i ?? uid('w'),
    x: (item.x ?? 0) * 2,
    y: (item.y ?? 0) * 3,
    w: (item.w ?? 4) * 2,
    h: (item.h ?? 3) * 3,
    viz:    legacy.viz,
    source: legacy.source,
    config: {
      ...defaultConfig(legacy.viz, legacy.source),
      ...(legacy.config ?? {}),
    },
  }
}

export function migrateV1Layout(layout) {
  return {
    version: 2,
    sections: [{
      id: uid('sec'),
      title: '',
      color: '',
      collapsed: false,
      layout: (layout ?? []).map(migrateV1Item).filter(Boolean),
    }],
  }
}

function makeWidget(viz, source, overrides = {}) {
  const size = VIZ_DEFAULT_SIZE[viz] ?? { w: 6, h: 6 }
  return {
    i: uid('w'),
    x: 0, y: 0,
    w: size.w, h: size.h,
    viz, source,
    config: defaultConfig(viz, source),
    ...overrides,
  }
}

// ── Default-Dashboard (v2) ───────────────────────────────────────────────────
export function buildDefaultLayout() {
  return {
    version: 2,
    sections: [
      {
        id: uid('sec'), title: '', color: '', collapsed: false,
        layout: [
          makeWidget('info', null, { x: 0, y: 0, w: 24, h: 9 }),
        ],
      },
      {
        id: uid('sec'), title: 'Überblick', color: '', collapsed: false,
        layout: [
          makeWidget('summary', 'workflows-summary', { x: 0,  y: 0, w: 8, h: 6 }),
          makeWidget('summary', 'posts-summary',     { x: 8,  y: 0, w: 8, h: 6 }),
          makeWidget('summary', 'groups-summary',    { x: 16, y: 0, w: 8, h: 6 }),
        ],
      },
      {
        id: uid('sec'), title: 'Threat News', color: '', collapsed: false,
        layout: [
          makeWidget('table', 'posts-latest',        { x: 0,  y: 0,  w: 12, h: 11 }),
          makeWidget('table', 'news-latest',         { x: 12, y: 0,  w: 12, h: 11 }),
          makeWidget('map',   'posts-countries-map', { x: 0,  y: 11, w: 24, h: 14 }),
        ],
      },
      {
        id: uid('sec'), title: 'Aktivität', color: '', collapsed: false,
        layout: [
          makeWidget('feed', 'hackwatch-activity', { x: 0,  y: 0, w: 12, h: 11 }),
          makeWidget('feed', 'siem-activity',      { x: 12, y: 0, w: 12, h: 11 }),
        ],
      },
    ],
  }
}

function normalizeLayout(raw) {
  if (raw && !Array.isArray(raw) && raw.version === 2 && Array.isArray(raw.sections)) {
    return raw
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return migrateV1Layout(raw)
  }
  return buildDefaultLayout()
}

const LAST_ACTIVE_KEY = 'secsu-dashboard-active-id'
const AUTOSAVE_MS     = 800

export const useDashboardStore = defineStore('dashboard', () => {
  // ── State ─────────────────────────────────────────────────────────────────
  const dashboards   = ref([])     // [{id, name, is_default, sort_order}] — ohne Layout
  const activeDashId = ref(null)
  const sections     = ref([])     // Sections des aktiven Dashboards
  const editMode     = ref(false)
  const loading      = ref(true)   // true bis zum ersten fetchDashboards()
  const saving       = ref(false)
  const dirty        = ref(false)

  let autosaveTimer = null

  // ── Derived ───────────────────────────────────────────────────────────────
  const activeDashboard = computed(
    () => dashboards.value.find(d => d.id === activeDashId.value) ?? null
  )

  function _layoutPayload() {
    return {
      version: 2,
      sections: sections.value.map(s => ({
        id: s.id, title: s.title, color: s.color, collapsed: s.collapsed,
        layout: s.layout.map(w => ({
          i: w.i, x: w.x, y: w.y, w: w.w, h: w.h,
          viz: w.viz, source: w.source, config: w.config ?? {},
        })),
      })),
    }
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  async function fetchDashboards() {
    // Spinner nur beim allerersten Laden — sonst würde das Grid unmounted,
    // bevor vue-grid-layouts resize-Listener feuern ("At least one element required").
    if (sections.value.length === 0) loading.value = true
    try {
      const { data } = await api.getDashboards()
      dashboards.value = data.dashboards ?? []

      const lastId = Number(localStorage.getItem(LAST_ACTIVE_KEY)) || null
      const target =
        dashboards.value.find(d => d.id === lastId) ??
        dashboards.value.find(d => d.is_default) ??
        dashboards.value[0]

      if (target) await _loadLayout(target.id)
    } catch {
      sections.value = buildDefaultLayout().sections
    } finally {
      loading.value = false
    }
  }

  async function _loadLayout(id) {
    const { data } = await api.getDashboard(id)
    activeDashId.value = data.id
    const normalized = normalizeLayout(data.layout)
    sections.value = normalized.sections
    dirty.value = false
    localStorage.setItem(LAST_ACTIVE_KEY, String(id))
    // Migriertes v1-Layout direkt persistieren, damit es stabil bleibt
    if (Array.isArray(data.layout) && data.layout.length > 0) {
      await saveActiveLayout()
    }
  }

  async function switchDashboard(id) {
    if (id === activeDashId.value) return
    if (dirty.value) await saveActiveLayout()
    loading.value = true
    try {
      await _loadLayout(id)
    } finally {
      loading.value = false
    }
  }

  // ── Save (mit Debounce-Auto-Save) ─────────────────────────────────────────
  async function saveActiveLayout() {
    if (!activeDashId.value) return
    if (autosaveTimer) { clearTimeout(autosaveTimer); autosaveTimer = null }
    saving.value = true
    try {
      await api.updateDashboard(activeDashId.value, { layout: _layoutPayload() })
      dirty.value = false
    } finally {
      saving.value = false
    }
  }

  function markDirty() {
    dirty.value = true
    if (autosaveTimer) clearTimeout(autosaveTimer)
    autosaveTimer = setTimeout(() => { saveActiveLayout() }, AUTOSAVE_MS)
  }

  // ── Create / rename / delete ──────────────────────────────────────────────
  async function createDashboard(name = 'Neues Dashboard') {
    const { data } = await api.createDashboard({
      name,
      layout: buildDefaultLayout(),
    })
    dashboards.value = [...dashboards.value, data]
    await _loadLayout(data.id)
  }

  async function renameDashboard(id, name) {
    const trimmed = (name ?? '').trim()
    if (!trimmed) return
    await api.updateDashboard(id, { name: trimmed })
    const d = dashboards.value.find(d => d.id === id)
    if (d) d.name = trimmed
  }

  async function deleteDashboard(id) {
    if (dashboards.value.length <= 1) return
    await api.deleteDashboard(id)
    dashboards.value = dashboards.value.filter(d => d.id !== id)
    if (activeDashId.value === id) {
      await _loadLayout(dashboards.value[0].id)
    }
  }

  // ── Edit mode ─────────────────────────────────────────────────────────────
  function toggleEditMode() { editMode.value = !editMode.value }
  function exitEditMode()   { editMode.value = false }

  // ── Section operations ────────────────────────────────────────────────────
  function findSection(sectionId) {
    return sections.value.find(s => s.id === sectionId) ?? null
  }

  function addSection(title = '') {
    sections.value = [
      ...sections.value,
      { id: uid('sec'), title, color: '', collapsed: false, layout: [] },
    ]
    markDirty()
  }

  function removeSection(sectionId) {
    sections.value = sections.value.filter(s => s.id !== sectionId)
    markDirty()
  }

  function updateSection(sectionId, patch) {
    const s = findSection(sectionId)
    if (!s) return
    Object.assign(s, patch)
    markDirty()
  }

  function moveSection(sectionId, dir) {
    const idx = sections.value.findIndex(s => s.id === sectionId)
    const to  = idx + dir
    if (idx < 0 || to < 0 || to >= sections.value.length) return
    const next = [...sections.value]
    ;[next[idx], next[to]] = [next[to], next[idx]]
    sections.value = next
    markDirty()
  }

  // ── Widget operations ─────────────────────────────────────────────────────
  function _collides(items, x, y, w, h) {
    return items.some(it => x < it.x + it.w && x + w > it.x && y < it.y + it.h && y + h > it.y)
  }

  // First-Fit: erste freie Lücke im Grid, die das Widget aufnehmen kann —
  // erst wenn keine passt, ans Ende setzen.
  function _placeFirstFree(section, widget) {
    const items = section.layout
    const w     = Math.min(widget.w, GRID_COLS)
    const maxY  = items.reduce((m, it) => Math.max(m, it.y + it.h), 0)
    for (let y = 0; y <= maxY; y++) {
      for (let x = 0; x + w <= GRID_COLS; x++) {
        if (!_collides(items, x, y, w, widget.h)) {
          widget.x = x
          widget.y = y
          return widget
        }
      }
    }
    widget.x = 0
    widget.y = maxY
    return widget
  }

  // Leeres Widget (viz=null) → DashWidget zeigt den Inline-Configurator
  function addEmptyWidget(sectionId) {
    const s = findSection(sectionId)
    if (!s) return
    const widget = _placeFirstFree(s, {
      i: uid('w'), x: 0, y: 0, w: 8, h: 8,
      viz: null, source: null, config: {},
    })
    s.layout = [...s.layout, widget]
    markDirty()
  }

  // Vorkonfiguriertes Widget (aus dem Vorlagen-Katalog)
  function addWidget(sectionId, viz, source, config = null) {
    const s = findSection(sectionId)
    if (!s) return
    const widget = _placeFirstFree(s, makeWidget(viz, source))
    if (config) widget.config = { ...widget.config, ...config }
    s.layout = [...s.layout, widget]
    markDirty()
  }

  // Widget duplizieren (gleiche Section, erster freier Platz)
  function cloneWidget(sectionId, widgetId) {
    const s = findSection(sectionId)
    const w = s?.layout.find(w => w.i === widgetId)
    if (!w) return
    const copy = _placeFirstFree(s, {
      i: uid('w'), x: 0, y: 0, w: w.w, h: w.h,
      viz: w.viz, source: w.source,
      config: JSON.parse(JSON.stringify(w.config ?? {})),
    })
    s.layout = [...s.layout, copy]
    markDirty()
  }

  function removeWidget(sectionId, widgetId) {
    const s = findSection(sectionId)
    if (!s) return
    s.layout = s.layout.filter(w => w.i !== widgetId)
    markDirty()
  }

  function updateWidget(sectionId, widgetId, patch) {
    const s = findSection(sectionId)
    const w = s?.layout.find(w => w.i === widgetId)
    if (!w) return
    if (patch.config) {
      const { config, ...rest } = patch
      w.config = { ...w.config, ...config }
      Object.assign(w, rest)
    } else {
      Object.assign(w, patch)
    }
    markDirty()
  }

  // vue-grid-layout kann nicht zwischen Grids draggen → Verschieben via Menü
  function moveWidgetToSection(widgetId, fromSectionId, toSectionId) {
    const from = findSection(fromSectionId)
    const to   = findSection(toSectionId)
    const w    = from?.layout.find(w => w.i === widgetId)
    if (!w || !to) return
    from.layout = from.layout.filter(x => x.i !== widgetId)
    to.layout   = [...to.layout, _placeFirstFree(to, w)]
    markDirty()
  }

  // vue-grid-layout mutiert Items in-place — nur dirty markieren
  function onLayoutUpdated() {
    markDirty()
  }

  function resetToDefault() {
    sections.value = buildDefaultLayout().sections
    markDirty()
  }

  return {
    dashboards, activeDashId, sections, activeDashboard,
    editMode, loading, saving, dirty,
    fetchDashboards, switchDashboard, saveActiveLayout,
    createDashboard, renameDashboard, deleteDashboard,
    toggleEditMode, exitEditMode,
    addSection, removeSection, updateSection, moveSection,
    addEmptyWidget, addWidget, cloneWidget, removeWidget, updateWidget,
    moveWidgetToSection, onLayoutUpdated, resetToDefault,
  }
})
